import type { ReactNode } from 'react';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useAuth } from '@/services/auth';
import { hasUnlimitedPlanFeatureAccess } from '@/services/auth/plan-features';
import type { PlanFeature } from '@/services/auth/types';
import { useParams } from '@/services/router/compat';

type RouteParams = Record<string, string | undefined>;

type PlanFeatureGuardProps = {
  feature: PlanFeature;
  redirectTo: string | ((params: RouteParams) => string);
  children: ReactNode;
};

/**
 * Renders children only when the account has access to the given plan feature.
 * Uses `hasUnlimitedPlanFeatureAccess` (not a raw `enabled` check) so premium
 * accounts — which can carry a gated feature as `enabled: false` — aren't
 * redirected off a feature they're entitled to. The Hydra-native equivalent of
 * the Ember `redirectAwayAndAlert` plan-feature gate.
 */
export function PlanFeatureGuard({ feature, redirectTo, children }: PlanFeatureGuardProps) {
  const { account } = useAuth();
  const params = useParams({ strict: false });

  if (!hasUnlimitedPlanFeatureAccess(account, feature)) {
    const resolvedRedirectTo = typeof redirectTo === 'function' ? redirectTo(params) : redirectTo;
    return <SafeNavigate to={resolvedRedirectTo} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return <>{children}</>;
}
